import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import ChartCard from './ChartCard';

interface StateTimeDistributionChartProps {
  data: Array<{
    estado: string;
    horario: string;
    quantidade: number;
    data: string;
  }>;
  title?: string;
  subtitle?: string;
}

const StateTimeDistributionChart: React.FC<StateTimeDistributionChartProps> = ({ 
  data, 
  title = 'Picos por Estado e Horário',
  subtitle = 'Distribuição de contratos criados por estado e horário'
}) => {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(44, 62, 80, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '12px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: {entry.value} contratos
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Função para agrupar estados em clusters (backend já retorna clusters corretos)
  const getClusterFromEstado = (estado: string): string => {
    const estadoUpper = estado.toUpperCase();
    // Backend já retorna os clusters corretos (PR, SC, SP, RJ/MG), apenas normalizar
    if (estadoUpper === 'RJ/MG' || estadoUpper === 'RJ' || estadoUpper === 'MG') {
      return 'RJ/MG';
    }
    if (estadoUpper === 'PR') {
      return 'PR';
    }
    if (estadoUpper === 'SC') {
      return 'SC';
    }
    if (estadoUpper === 'SP') {
      return 'SP';
    }
    // Se vier algo inesperado, ignorar (não criar "Outros")
    return estado;
  };

  // Obter todos os horários únicos para criar as barras (ordenados decrescentemente)
  const allHours = useMemo(() => {
    const hours = new Set<string>();
    data.forEach(item => hours.add(item.horario));
    return Array.from(hours).sort((a, b) => {
      // Extrair hora numérica para ordenação (ex: "11:00" -> 11)
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourB - hourA; // Ordem decrescente
    });
  }, [data]);

  // Agrupar dados por cluster e horário
  const chartData = useMemo(() => {
    const grouped = new Map<string, { estado: string; [key: string]: number | string }>();
    
    data.forEach(item => {
      const cluster = getClusterFromEstado(item.estado);
      if (!grouped.has(cluster)) {
        grouped.set(cluster, { estado: cluster });
      }
      const clusterData = grouped.get(cluster)!;
      clusterData[item.horario] = (clusterData[item.horario] as number || 0) + item.quantidade;
    });

    // Ordenar clusters: PR, SC, SP, RJ/MG (apenas os 4 clusters principais)
    const order = ['PR', 'SC', 'SP', 'RJ/MG'];
    const sorted = Array.from(grouped.values())
      .filter(item => order.includes(item.estado as string))
      .sort((a, b) => {
        const indexA = order.indexOf(a.estado as string);
        const indexB = order.indexOf(b.estado as string);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

    return sorted;
  }, [data]);

  // Calcular totais por cluster para resumo
  const clusterTotals = useMemo(() => {
    const totals = new Map<string, number>();
    chartData.forEach(cluster => {
      const total = allHours.reduce((sum, hour) => {
        return sum + ((cluster[hour] as number) || 0);
      }, 0);
      totals.set(cluster.estado as string, total);
    });
    return totals;
  }, [chartData, allHours]);

  // Cores para diferentes horários
  const colors = [
    '#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6',
    '#1abc9c', '#34495e', '#e67e22', '#16a085', '#27ae60',
    '#2980b9', '#8e44ad', '#c0392b', '#d35400', '#7f8c8d',
    '#f1c40f', '#e91e63', '#00bcd4', '#ff9800', '#4caf50',
    '#2196f3', '#9c27b0', '#ff5722', '#607d8b', '#795548'
  ];

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <div style={{ display: 'flex', width: '100%', gap: '20px' }}>
        {/* Legendas à esquerda */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          minWidth: '200px',
          width: '200px',
          maxHeight: '500px',
          overflowY: 'auto',
          padding: '12px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          flexShrink: 0
        }}>
          <div style={{ 
            fontWeight: 600, 
            marginBottom: '12px', 
            fontSize: '14px',
            color: 'rgba(0, 0, 0, 0.95)',
            whiteSpace: 'nowrap'
          }}>
            Horários:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {allHours.map((hour, index) => (
              <div 
                key={hour} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
              >
                <div 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    backgroundColor: colors[index % colors.length],
                    borderRadius: '3px',
                    flexShrink: 0,
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }} 
                />
                <span style={{ color: 'rgba(0, 0, 0, 0.95)' }}>{hour}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gráfico */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart 
              data={chartData} 
              margin={{ top: 80, right: 30, left: 20, bottom: 60 }}
              barCategoryGap="10%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="estado" 
                stroke="rgba(255, 255, 255, 0.9)"
                angle={0}
                textAnchor="middle"
                height={80}
                interval={0}
                tick={(props: any) => {
                  const { x, y, payload } = props;
                  const estado = payload.value;
                  const total = clusterTotals.get(estado) || 0;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={16}
                        textAnchor="middle"
                        fill="rgba(0, 0, 0, 0.95)"
                        fontSize="13px"
                        fontWeight={600}
                      >
                        {estado}
                      </text>
                      <text
                        x={0}
                        y={0}
                        dy={32}
                        textAnchor="middle"
                        fill="rgba(0, 0, 0, 0.75)"
                        fontSize="11px"
                        fontWeight={500}
                      >
                        {total} contratos
                      </text>
                    </g>
                  );
                }}
              />
              <YAxis 
                stroke="rgba(255, 255, 255, 0.7)"
                style={{ fontSize: '12px' }}
                tick={{ fill: 'rgba(255, 255, 255, 0.9)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              {allHours.map((hour, index) => (
                <Bar 
                  key={hour}
                  dataKey={hour} 
                  stackId="a"
                  fill={colors[index % colors.length]}
                  name={hour}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          
          {/* Resumo abaixo do gráfico */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            marginTop: '10px',
            padding: '8px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            {chartData.map((cluster) => {
              const total = clusterTotals.get(cluster.estado as string) || 0;
              return (
                <div 
                  key={cluster.estado as string}
                  style={{
                    textAlign: 'center',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    minWidth: '80px'
                  }}
                >
                  <div style={{ 
                    fontSize: '11px', 
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '4px'
                  }}>
                    {cluster.estado}
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    {total}
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: 'rgba(255, 255, 255, 0.6)'
                  }}>
                    contratos
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ChartCard>
  );
};

export default StateTimeDistributionChart;

